import { useState, useEffect, useCallback } from "react";
import Layout from "../components/Layout";
import { api, toast } from "../App";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Loader2, ShieldCheck, CheckCircle2, RotateCw, History, AlertTriangle } from "lucide-react";
import { fh } from "@/utils/hours";

export default function CheckingQueue({ user }) {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [projectFilter, setProjectFilter] = useState("all");
  const [cycleFilter, setCycleFilter] = useState("all");
  const [query, setQuery] = useState("");
  
  // Modals
  const [sendBackTask, setSendBackTask] = useState(null);
  const [sendBackComments, setSendBackComments] = useState("");
  const [sendBackHours, setSendBackHours] = useState("");
  const [approveTask, setApproveTask] = useState(null);
  const [approveHours, setApproveHours] = useState("");
  const [historyTask, setHistoryTask] = useState(null);
  const [saving, setSaving] = useState(false);

  const fetchQueue = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/planning/checking-queue");
      setTasks(res.data?.tasks || []);
    } catch {
      toast.error("Failed to load checking queue");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchQueue(); }, [fetchQueue]);

  const handleApprove = async () => {
    if (!approveTask) return;
    const hrs = parseFloat(approveHours);
    if (!hrs || hrs <= 0) { toast.error("Please enter the hours you spent checking"); return; }
    setSaving(true);
    try {
      await api.post(`/planning/tasks/${approveTask.checker_task_id}/approve-check`, {
        hours_spent: hrs,
        log_date: new Date().toISOString().slice(0, 10),
        note: "Approved",
      });
      toast.success("Task approved and closed");
      setApproveTask(null);
      setApproveHours("");
      fetchQueue();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to approve");
    } finally {
      setSaving(false);
    }
  };

  const handleSendBack = async () => {
    if (!sendBackTask) return;
    if (!sendBackComments.trim()) { toast.error("Comments are required"); return; }
    const hrs = parseFloat(sendBackHours);
    if (!hrs || hrs <= 0) { toast.error("Please enter the hours you spent checking"); return; }
    setSaving(true);
    try {
      await api.post(`/planning/tasks/${sendBackTask.checker_task_id}/send-back`, {
        comments: sendBackComments.trim(),
        hours_spent: hrs,
        log_date: new Date().toISOString().slice(0, 10),
      });
      toast.success(`Sent back for rework (cycle ${(sendBackTask.current_cycle || 1) + 1})`);
      setSendBackTask(null);
      setSendBackComments("");
      setSendBackHours("");
      fetchQueue();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to send back");
    } finally {
      setSaving(false);
    }
  };

  const wfBadge = (wf) => {
    if (wf === "ready_for_check") return <Badge className="bg-amber-100 text-amber-700 border border-amber-200">Awaiting Check</Badge>;
    if (wf === "checking") return <Badge className="bg-blue-100 text-blue-700 border border-blue-200">Checking</Badge>;
    return <Badge variant="outline">{wf || "-"}</Badge>;
  };

  // Build filter options
  const projects = Array.from(new Set(tasks.map(t => `${t.project_number}|${t.project_name}`))).filter(Boolean);
  
  const filtered = tasks.filter(t => {
    if (projectFilter !== "all" && `${t.project_number}|${t.project_name}` !== projectFilter) return false;
    if (cycleFilter === "rework" && (t.current_cycle || 1) <= 1) return false;
    if (cycleFilter === "first" && (t.current_cycle || 1) > 1) return false;
    if (query.trim()) {
      const q = query.toLowerCase();
      if (!(t.description || "").toLowerCase().includes(q)
        && !(t.assigned_user_name || "").toLowerCase().includes(q)
        && !(t.project_number || "").toLowerCase().includes(q)) return false;
    }
    return true;
  });

  return (
    <Layout user={user}>
      <div className="p-6 space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
          <div>
            <h1 className="text-md3-display-small font-semibold text-md3-on-surface flex items-center gap-3">
              <ShieldCheck className="h-7 w-7 text-md3-primary" />
              Checking Queue
            </h1>
            <p className="text-md3-on-surface-variant text-sm">Tasks awaiting your review across all projects.</p>
          </div>
          <Badge className="bg-md3-primary/10 text-md3-primary border-md3-primary text-base px-4 py-2" data-testid="queue-count-badge">
            {filtered.length} {filtered.length === 1 ? "task" : "tasks"} pending
          </Badge>
        </div>

        <Card className="shadow-md3-1 rounded-md3-lg border-0 bg-md3-surface-container">
          <CardHeader className="pb-3">
            <CardTitle className="text-md3-title-large">Filters</CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <label className="text-xs text-md3-on-surface-variant font-medium">Project</label>
              <Select value={projectFilter} onValueChange={setProjectFilter}>
                <SelectTrigger className="bg-white" data-testid="queue-project-filter"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Projects</SelectItem>
                  {projects.map(p => {
                    const [num, name] = p.split("|");
                    return <SelectItem key={p} value={p}>{num} — {name}</SelectItem>;
                  })}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs text-md3-on-surface-variant font-medium">Cycle</label>
              <Select value={cycleFilter} onValueChange={setCycleFilter}>
                <SelectTrigger className="bg-white" data-testid="queue-cycle-filter"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Cycles</SelectItem>
                  <SelectItem value="first">First-Pass Only</SelectItem>
                  <SelectItem value="rework">Rework (Cycle 2+)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs text-md3-on-surface-variant font-medium">Search</label>
              <Input
                placeholder="Task description, producer name, project #"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                className="bg-white"
                data-testid="queue-search"
              />
            </div>
          </CardContent>
        </Card>

        <Card className="shadow-md3-1 rounded-md3-lg border-0 bg-md3-surface-container">
          <CardContent className="p-0">
            {loading ? (
              <div className="flex justify-center py-12"><Loader2 className="h-7 w-7 animate-spin text-md3-primary" /></div>
            ) : filtered.length === 0 ? (
              <div className="text-center py-14 text-md3-on-surface-variant">
                <ShieldCheck className="h-10 w-10 mx-auto mb-2 text-md3-on-surface-variant/50" />
                <p className="text-sm">No tasks awaiting your check.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-md3-surface-container-high border-b border-md3-outline-variant">
                    <tr className="text-left">
                      <th className="px-3 py-2 font-semibold">Project</th>
                      <th className="px-3 py-2 font-semibold">Activity</th>
                      <th className="px-3 py-2 font-semibold">Task</th>
                      <th className="px-3 py-2 font-semibold">Producer</th>
                      <th className="px-3 py-2 font-semibold text-right">Hours</th>
                      <th className="px-3 py-2 font-semibold">Cycle</th>
                      <th className="px-3 py-2 font-semibold">State</th>
                      <th className="px-3 py-2 font-semibold text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map(t => (
                      <tr key={t.producer_task_id} data-testid={`queue-row-${t.producer_task_id}`} className="border-b border-md3-outline-variant hover:bg-md3-surface-container-low">
                        <td className="px-3 py-2">
                          <div className="font-mono text-xs text-md3-primary">{t.project_number}</div>
                          <div className="text-xs text-md3-on-surface-variant truncate max-w-[160px]" title={t.project_name}>{t.project_name}</div>
                        </td>
                        <td className="px-3 py-2">
                          <Badge variant="outline" className="capitalize text-xs">{(t.producer_activity_type || "").replace(/_/g, " ")}</Badge>
                        </td>
                        <td className="px-3 py-2 max-w-xs truncate" title={t.description}>{t.description}</td>
                        <td className="px-3 py-2 text-xs">{t.assigned_user_name || "Unassigned"}</td>
                        <td className="px-3 py-2 text-right whitespace-nowrap">
                          <span className="font-medium">{fh(t.actual_hours)}</span>
                          <span className="text-md3-on-surface-variant"> / {fh(t.allocated_hours)}</span>
                        </td>
                        <td className="px-3 py-2">
                          {(t.current_cycle || 1) > 1 ? (
                            <Badge className="bg-orange-100 text-orange-700 border border-orange-200 gap-1">
                              <AlertTriangle className="h-3 w-3" />
                              Cycle {t.current_cycle}
                            </Badge>
                          ) : (
                            <Badge variant="outline" className="text-xs">Cycle 1</Badge>
                          )}
                        </td>
                        <td className="px-3 py-2">{wfBadge(t.workflow_state)}</td>
                        <td className="px-3 py-2 text-right whitespace-nowrap">
                          <div className="inline-flex gap-1">
                            {(t.rework_cycles || []).length > 0 && (
                              <Button size="sm" variant="ghost" className="h-7 px-2" onClick={() => setHistoryTask(t)} data-testid={`queue-history-${t.producer_task_id}`}>
                                <History className="h-3.5 w-3.5 mr-1" />
                                History
                              </Button>
                            )}
                            <Button size="sm" variant="outline" className="h-7 border-orange-300 text-orange-700 hover:bg-orange-50" onClick={() => setSendBackTask(t)} data-testid={`queue-sendback-${t.producer_task_id}`}>
                              <RotateCw className="h-3.5 w-3.5 mr-1" />
                              Send Back
                            </Button>
                            <Button size="sm" className="h-7 bg-green-600 hover:bg-green-700 text-white" onClick={() => setApproveTask(t)} data-testid={`queue-approve-${t.producer_task_id}`}>
                              <CheckCircle2 className="h-3.5 w-3.5 mr-1" />
                              Approve
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Send Back Dialog */}
      <Dialog open={!!sendBackTask} onOpenChange={(o) => !o && setSendBackTask(null)}>
        <DialogContent className="bg-white max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <RotateCw className="h-5 w-5 text-orange-600" />
              Send Back for Rework
            </DialogTitle>
            <DialogDescription>
              {sendBackTask?.description}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="text-xs text-md3-on-surface-variant bg-amber-50 border border-amber-200 rounded p-2">
              <strong>Note:</strong> The producer will receive this task back as Cycle {(sendBackTask?.current_cycle || 1) + 1}.
              Any rework hours they log will be tracked as actual but won't earn additional credit.
            </div>
            <div>
              <label className="text-sm font-medium">Hours You Spent Checking *</label>
              <Input
                type="number"
                step="0.25"
                min="0.25"
                value={sendBackHours}
                onChange={(e) => setSendBackHours(e.target.value)}
                placeholder="e.g. 0.5"
                className="bg-white"
                data-testid="sendback-hours"
              />
              <p className="text-xs text-md3-on-surface-variant mt-1">Logged as a checker work-log on today's date.</p>
            </div>
            <div>
              <label className="text-sm font-medium">Comments *</label>
              <Textarea
                value={sendBackComments}
                onChange={(e) => setSendBackComments(e.target.value)}
                placeholder="Describe what needs to be corrected..."
                rows={5}
                className="bg-white"
                data-testid="sendback-comments"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setSendBackTask(null); setSendBackHours(""); }}>Cancel</Button>
            <Button onClick={handleSendBack} disabled={saving || !sendBackComments.trim() || !parseFloat(sendBackHours)} className="bg-orange-600 hover:bg-orange-700 text-white" data-testid="sendback-submit">
              {saving ? "Sending..." : "Send Back with Comments"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Approve Dialog */}
      <Dialog open={!!approveTask} onOpenChange={(o) => !o && setApproveTask(null)}>
        <DialogContent className="bg-white max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle2 className="h-5 w-5 text-green-600" />
              Approve & Close
            </DialogTitle>
            <DialogDescription>{approveTask?.description}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="text-xs text-md3-on-surface-variant bg-green-50 border border-green-200 rounded p-2">
              Both producer and checker tasks will close. Earned hours snapshot.
            </div>
            <div>
              <label className="text-sm font-medium">Hours You Spent Checking *</label>
              <Input
                type="number"
                step="0.25"
                min="0.25"
                value={approveHours}
                onChange={(e) => setApproveHours(e.target.value)}
                placeholder="e.g. 1.5"
                className="bg-white"
                data-testid="approve-hours"
              />
              <p className="text-xs text-md3-on-surface-variant mt-1">Logged as a checker work-log on today's date.</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setApproveTask(null); setApproveHours(""); }}>Cancel</Button>
            <Button onClick={handleApprove} disabled={saving || !parseFloat(approveHours)} className="bg-green-600 hover:bg-green-700 text-white" data-testid="approve-submit">
              {saving ? "Approving..." : "Approve & Close"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* History Dialog */}
      <Dialog open={!!historyTask} onOpenChange={(o) => !o && setHistoryTask(null)}>
        <DialogContent className="bg-white max-w-xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="h-5 w-5 text-md3-primary" />
              Rework History — Cycle {historyTask?.current_cycle}
            </DialogTitle>
            <DialogDescription>{historyTask?.description}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 max-h-[60vh] overflow-y-auto">
            {(historyTask?.rework_cycles || []).map((c, i) => (
              <div key={i} className="border border-orange-200 bg-orange-50 rounded p-3 text-sm">
                <div className="flex justify-between items-center mb-1">
                  <Badge className="bg-orange-100 text-orange-700">Cycle {c.cycle}</Badge>
                  <span className="text-xs text-md3-on-surface-variant">
                    {c.sent_back_by_name} · {c.sent_back_at ? new Date(c.sent_back_at).toLocaleString() : ""}
                  </span>
                </div>
                <div className="text-md3-on-surface whitespace-pre-wrap">{c.comments}</div>
              </div>
            ))}
            {(historyTask?.rework_cycles || []).length === 0 && (
              <div className="text-sm text-md3-on-surface-variant text-center py-4">No rework cycles yet.</div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setHistoryTask(null)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
